import { useUserSession } from '@solinkcorp/authentication';
import { type DeviceId } from '@solinkcorp/callhome';
import settings from '@solinkcorp/settings';
import { useLatest } from '@solinkcorp/solink-components';
import { useCallback, useEffect } from 'react';
import { type LiveFeedHeartbeat, type LiveFeedTelemetry } from '../analytics/liveFeedTelemetry';
import { getSdmAuthSessionForHeartbeat } from '../authSessionStorage';
import { type SSEConnectionState } from './useServerSentEvents';

const METRICS_HEARTBEAT_MS = 60 * 1000;

interface MetricsHeartbeatConnection {
  deviceId: DeviceId;
  sseStatus: SSEConnectionState['status'];
  lastEventReceivedAt: string | null;
  avgEventLatencyMs: number | null;
}

interface MetricsHeartbeatPayload {
  connections: MetricsHeartbeatConnection[];
  liveFeeds: LiveFeedHeartbeat[];
}

async function postMetricsHeartbeat(payload: MetricsHeartbeatPayload, jwt: string) {
  try {
    const response = await fetch(`${settings.cloudAIHost}/sdm/heartbeat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        ...payload,
        authSession: getSdmAuthSessionForHeartbeat() ?? undefined,
      }),
    });

    if (!response.ok) {
      throw new Error(`SDM heartbeat request failed: ${response.status}`);
    }

    await response.json();
  } catch (err) {
    throw new Error(`SDM heartbeat request failed: ${err}`);
  }
}

export default function useServiceDisplayHeartbeat(
  deviceId: DeviceId | undefined,
  sseStatus: SSEConnectionState['status'],
  lastEventReceivedAt: string | null,
  consumeAvgEventLatencyMs: () => number | null,
  liveFeedTelemetry: LiveFeedTelemetry,
  // Only the standalone SDM app reports heartbeats; the embedded web view must not.
  isStandalone: boolean,
) {
  const userSession = useUserSession();
  const accessToken = userSession?.accessToken;
  const latestSseStatus = useLatest(sseStatus);
  const latestLastEventReceivedAt = useLatest(lastEventReceivedAt);
  const latestConsumeAvgEventLatencyMs = useLatest(consumeAvgEventLatencyMs);

  const sendHeartbeat = useCallback(
    async (id: DeviceId) => {
      return await postMetricsHeartbeat(
        {
          connections: [
            {
              deviceId: id,
              sseStatus: latestSseStatus.current,
              lastEventReceivedAt: latestLastEventReceivedAt.current,
              // Drains the accumulated window so each heartbeat reports only its own interval.
              avgEventLatencyMs: latestConsumeAvgEventLatencyMs.current(),
            },
          ],
          liveFeeds: liveFeedTelemetry.drain(),
        },
        accessToken,
      );
    },
    [
      accessToken,
      latestSseStatus,
      latestLastEventReceivedAt,
      latestConsumeAvgEventLatencyMs,
      liveFeedTelemetry,
    ],
  );

  useEffect(() => {
    if (!isStandalone || !accessToken || !deviceId) return;

    let interval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    const cleanupHeartbeat = () => {
      cancelled = true;
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };

    const sendHeartbeatWithInterval = async () => {
      try {
        await sendHeartbeat(deviceId);
        if (cancelled) return;

        interval = setInterval(async () => {
          try {
            await sendHeartbeat(deviceId);
          } catch {
            cleanupHeartbeat();
          }
        }, METRICS_HEARTBEAT_MS);
      } catch {
        cleanupHeartbeat();
      }
    };

    sendHeartbeatWithInterval();

    return cleanupHeartbeat;
  }, [isStandalone, accessToken, deviceId, sendHeartbeat]);
}
